import React, { useState, useEffect } from "react";
import { Link } from "gatsby";
import ButtonInternal from "../buttons/buttonInternal";
import { RenderRichContent } from "../RenderRichContent/RenderRichContent";
import * as styles from "./newSidedrawer.module.scss";
import ButtonExternal from "../buttons/buttonExternal";

const SideDrawer = ({ data, onClick, urlContent, showSidebar }) => {
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [langOpen, setLangOpen] = useState(false);

  // Prevent body scrolling when sidebar is open
  useEffect(() => {
    if (showSidebar) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    // Cleanup function to reset overflow when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showSidebar]);

  // Calculate URL elements
  const getUrlElements = () => {
    const locale = data.node_locale;
    const urlMap = {
      de: [0, 1, 3],
      nl: [1, 2, 3],
      en: [0, 2, 3],
      "en-US": [1, 0, 2],
    };

    const indices = urlMap[locale] || [0, 1, 2];
    return indices.map((index) => urlContent[index] || "");
  };

  const [firstURLElement, secondURLElement, thirdURLElement] = getUrlElements();

  const toggleDropdown = (index) => {
    setOpenDropdowns((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  return (
    <nav
      className={`fixed h-full top-0 right-0 bg-white w-full xl:hidden pl-3 pr-2 pt-2 p-6 sm:p-8 z-50 transition-all duration-300 transform overflow-y-auto ${
        showSidebar ? "translate-x-0" : "translate-x-full"
      }`}
    >
      {/* Header with logo and close button */}
      <div className="flex justify-between items-center mb-10">
        <div className="flex items-center mt-0.5">
          <img
            src="//images.ctfassets.net/rh7phaxwpmak/Ns1AXi5L7eEbJaasO6CJY/958609d8da75d9ca0c7e79ca94b88cc2/Group_3_3x.png"
            alt="Construction App for Project Management"
            className="h-[60px] w-[60px]"
          />
        </div>
        <div className="flex items-center mb-5">
          <span className="text-sm font-medium mr-4"></span>

          <button onClick={onClick} className="text-black-500 -mt-[8px]">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-8 h-8"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Scrollable content container */}
      <div className="h-200px overflow-y-auto pb-4">
        {/* Main Navigation Links */}
        <div className="mb-8">
        {data.headerLinks.map((headerlink, index) => {
          const hasSubmenu = headerlink.menuItemSubs;
          const isOpen = openDropdowns[index];
          
          return hasSubmenu ? (
            <div key={`link-${index}`} className="py-3">
              <div 
                className="flex justify-between items-center cursor-pointer"
                onClick={() => toggleDropdown(index)}
              >
                {/* Added conditional font-bold class here */}
                <div className={isOpen ? "font-bold text-base" : ""}>
                  <RenderRichContent
                    content={headerlink.menuItemNameLink}
                    role="side-drawer-link"
                  />
                </div>
                <span className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </span>
              </div>
                
              {isOpen && headerlink.menuItemSubs && (
                <div className={`${styles.subMenuImages} pl-4 mt-4`}>
                  {headerlink.menuItemSubs.map((subMenuItem, subIndex) => {
                    // Handle nested subcategory structure
                    if (subMenuItem.subCategoryMenuItems) {
                      return (
                        <div key={`subcat-${subIndex}`} className="mb-3">
                          {/* Subcategory heading */}
                          <div className="font-semibold text-gray-700 mb-1">
                            <RenderRichContent
                              content={subMenuItem.subCategoryMenuItemsHeading}
                              role="subheader"
                            />
                          </div>
                          
                          {/* Subcategory items */}
                          <div className="pl-3">
                            {subMenuItem.subCategoryMenuItems.map(
                              (subCategory, subCategoryIndex) => (
                                <div 
                                  key={`subitem-${subCategoryIndex}`} 
                                  className="py-1"
                                >
                                  <RenderRichContent
                                    content={subCategory.menuItemNameLink}
                                    role="side-drawer-link"
                                  />
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      );
                    }
                    
                    // Regular submenu item
                    return (
                      <div key={`sub-${subIndex}`} className="py-2 flex item-center space-x-4">
                        <RenderRichContent
                          content={subMenuItem.menuItemNameLink}
                          role="side-drawer-link"
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div key={`link-${index}`} className="py-3">
              <RenderRichContent
                content={headerlink.menuItemNameLink}
                role="side-drawer-link"
              />
            </div>
          );
        })}
      </div>

        {/* Login Link */}
        <div className="mb-2">
          <a
            href={data.loginLink}
            target="_blank"
            rel="noreferrer"
            className="block py-3 text-center font-bold text-black transition-colors text-base"
          >
            {data.loginLinkText}
          </a>
        </div>

        {/* Try for Free Button */}
        <div className="w-full mb-10 px-4 flex justify-center">
          {data.menuButtons.map((button, index) =>
            button.internalPageReference ? (
              <ButtonInternal
                data={button}
                key={`mobile-btn-${index}`}
                display="bg-blue-600 hover:bg-[#00469d] text-white !font-bold py-4 px-6 rounded-full transition-all text-center"
              />
            ) : (
              <ButtonExternal
                data={button}
                key={`mobile-btn-${index}`}
                display="bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-6 rounded-full transition-colors text-center"
              />
            )
          )}
        </div>
      </div>

      {/* Bottom Links - Register, Support, Language */}
      <div className="flex justify-between items-center pt-4">
        <a
          href={data.registerLink}
          target="_blank"
          rel="noreferrer"
          className="text-base font-medium text-[#9ea2a7]"
        >
          {data.registerLinkText}
        </a>

        <a
          href={data.supportLink}
          target="_blank"
          rel="noreferrer"
          className="text-base font-medium text-[#9ea2a7]"
        >
          {data.supportLinkText}
        </a>

        <div className="relative">
          <div
            className="flex items-center cursor-pointer"
            onClick={() => setLangOpen(!langOpen)}
          >
            <span className="text-base font-medium mr-1 text-[#9ea2a7]">
              {data.currentLanguage}
            </span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-4 w-4 transition-transform duration-200 ${
                langOpen ? "rotate-180" : ""
              } text-[#9ea2a7]`}
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </div>

          {langOpen && (
            <div className="absolute right-0 mt-2 w-32 bg-white shadow-lg rounded-md z-10">
              {data.languageSwitchUrls.map((url, i) => (
                <a
                  key={`lang-${i}`}
                  href={`${url}/${
                    [firstURLElement, secondURLElement, thirdURLElement][i]
                  }`}
                  className="block px-4 py-2 text-sm hover:bg-gray-100 text-[#9ea2a7]"
                >
                  {data.languageSwitchLanguageCode[i]}
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default SideDrawer;